/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Form, Grid } from '@mairistem/composants';

import {
  CODES_ETAT_ANTERIEUR_PACS,
  DATE_FORMAT,
  OPTIONS_SEXE_LETTRE,
  URL_ETATS_MATRIMONIAUX,
  regleEtatMatrimonial,
} from '@/constants';
import { useOptionsReferentiel } from '@/hooks';

interface ChampsEtatMatrimonialProps {
  /** Objet portant les champs à plat (`etatMatrimonialCode`, `nomExConjoint`…). */
  data: Record<string, any>;
  errors?: Record<string, string>;
  onChange: (event: React.SyntheticEvent | null, payload: any) => void;
  /** Charge le référentiel ; une modale ne l'active qu'à son ouverture. */
  actif?: boolean;
  /** Format des dates émises : l'assistant saisit en `DD/MM/YYYY`, la fiche en ISO. */
  formatDateSortie?: string;
}

/**
 * Champs de la situation matrimoniale du défunt, affichés selon la règle de la
 * situation choisie (cf. `constants/etatMatrimonialDeces`).
 *
 * Célibataire et union libre n'admettent aucun champ complémentaire ; trois
 * situations portent une date, dont le libellé varie ; le Pacs remplace cette
 * date par la situation qui le précédait et parle d'« ex-partenaire ».
 *
 * Partagé par l'assistant et la modale de la fiche : la règle vit en un seul
 * endroit, et l'API applique la même à l'écriture — un champ masqué après coup
 * y est purgé, l'IHM n'a donc pas à nettoyer elle-même.
 */
const ChampsEtatMatrimonial = (props: ChampsEtatMatrimonialProps) => {
  const {
    data,
    errors = {},
    onChange,
    actif = true,
    formatDateSortie = DATE_FORMAT,
  } = props;

  const { options, items } = useOptionsReferentiel(
    actif ? URL_ETATS_MATRIMONIAUX : undefined,
    'codeJustice',
  );

  const regle = regleEtatMatrimonial(data?.etatMatrimonialCode);

  /** Situations proposées comme antérieures à un Pacs : sous-ensemble du même. */
  const optionsAnterieures = React.useMemo(
    () =>
      options.filter(({ value }) =>
        CODES_ETAT_ANTERIEUR_PACS.includes(String(value)),
      ),
    [options],
  );

  /**
   * `ad_df_ematl` est une dénormalisation du libellé : on la dérive de l'option
   * choisie. Les deux `onChange` s'enchaînent sans risque, l'écriture passant
   * par un `setData(prev => …)`.
   */
  const handleSituationChange = React.useCallback(
    (
      event: React.SyntheticEvent | null,
      { name, value }: { name: string; value: unknown },
    ) => {
      onChange(event, { name, value });

      const item = _.find(items, { codeJustice: value });

      onChange(null, {
        name:
          name === 'etatMatrimonialCodePacs'
            ? 'etatMatrimonialLibellePacs'
            : 'etatMatrimonialLibelle',
        value: item?.libelle ?? null,
      });
    },
    [onChange, items],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      onChange(null, {
        name: 'dateEtatMatrimonial',
        value: _.isNil(date) ? null : dayjs(date).format(formatDateSortie),
      });
    },
    [onChange, formatDateSortie],
  );

  const role =
    regle.roleConjoint === 'partenaire' ? 'ex-partenaire' : 'ex-conjoint';

  return (
    <Grid>
      <Grid.Row style={{ paddingLeft: '1em' }}>
        <Grid.Column computer={8} tablet={16} mobile={16}>
          <Form.Dropdown
            selection
            clearable
            fluid
            name="etatMatrimonialCode"
            label="Etat matrimonial"
            placeholder="Choisir une situation"
            options={options}
            value={data?.etatMatrimonialCode ?? ''}
            error={!_.isNil(errors?.etatMatrimonialCode)}
            onChange={handleSituationChange}
          />
        </Grid.Column>
        {regle.libelleDate && (
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Form.Field error={!_.isNil(errors?.dateEtatMatrimonial)}>
              <label>{regle.libelleDate}</label>
              <DateInput
                value={
                  data?.dateEtatMatrimonial
                    ? dayjs(data.dateEtatMatrimonial, formatDateSortie).toDate()
                    : undefined
                }
                onChange={handleDateChange}
              />
            </Form.Field>
          </Grid.Column>
        )}
        {regle.etatAnterieurPacs && (
          <Grid.Column computer={8} tablet={16} mobile={16}>
            <Form.Dropdown
              selection
              clearable
              fluid
              name="etatMatrimonialCodePacs"
              label="Situation avant le Pacs"
              placeholder="Choisir une situation"
              options={optionsAnterieures}
              value={data?.etatMatrimonialCodePacs ?? ''}
              onChange={handleSituationChange}
            />
          </Grid.Column>
        )}
      </Grid.Row>
      {regle.roleConjoint && (
        <Grid.Row style={{ paddingLeft: '1em' }}>
          <Grid.Column computer={5} tablet={8} mobile={16}>
            <Form.Dropdown
              selection
              clearable
              fluid
              // `upward` : ce champ est le dernier du formulaire, une liste qui
              // s'ouvre vers le bas sort du cadre visible.
              upward
              name="sexeExConjoint"
              label={`Sexe ${role}`}
              placeholder="Sexe"
              options={OPTIONS_SEXE_LETTRE}
              value={data?.sexeExConjoint ?? ''}
              onChange={onChange}
            />
          </Grid.Column>
          <Grid.Column computer={5} tablet={8} mobile={16}>
            <Form.Input
              fluid
              label={`Nom ${role}`}
              name="nomExConjoint"
              // ec_actd.ad_df_nomex est un varchar(50).
              maxLength={50}
              value={data?.nomExConjoint ?? ''}
              onChange={onChange}
            />
          </Grid.Column>
          <Grid.Column computer={6} tablet={16} mobile={16}>
            <Form.Input
              fluid
              label={`Prénom ${role}`}
              name="prenomExConjoint"
              // ec_actd.ad_df_prenex est un varchar(100).
              maxLength={100}
              value={data?.prenomExConjoint ?? ''}
              onChange={onChange}
            />
          </Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

export default ChampsEtatMatrimonial;
